import { useCallback, useState } from 'react';

import { useSignupModalStyles as usePartnerSignupStyles } from '@/auth/components/shared/useSignupModalStyles';
import Button from '@/components/Button';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import type { DomainMatch } from '../types';
import { mapTeamJoinError } from '../utils/teamJoinError';
import { useStyles as useTeamJoinStyles } from './TeamJoinStep.styles';

interface TeamJoinStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  // requestToJoinGroup accepts `otpVerificationToken` as optional in the
  // GraphQL schema and falls back to Bearer auth from the user's session.
  // On resume the in-memory OTP token is gone but the user is already
  // authenticated, so we must not gate the call on its presence.
  onRequestToJoin: (input: {
    groupId: string;
    otpVerificationToken?: string;
  }) => Promise<{ autoApproved: boolean }>;
}

export function TeamJoinStep({ flow, onRequestToJoin }: TeamJoinStepProps): JSX.Element {
  const s = usePartnerSignupStyles();
  const styles = useTeamJoinStyles();
  const { state } = flow;
  const [busy, setBusy] = useState(false);

  // On success, transition the flow to 'team-request-sent' so the
  // consolidated TeamRequestSentStep renders the appropriate copy
  // (auto-approved vs awaiting approval) in one place.
  const requestJoin = useCallback(
    async (team: DomainMatch) => {
      try {
        setBusy(true);
        const { autoApproved } = await onRequestToJoin({
          groupId: team.id,
          // OTP token is opportunistic: present during the fresh
          // (single-session) signup flow; absent on resume where Bearer
          // auth carries the identity. Omitting it is the documented
          // fallback path.
          ...(state.emailOtpToken ? { otpVerificationToken: state.emailOtpToken } : {}),
        });
        flow.joinRequested(team, autoApproved);
      } catch (err) {
        const { code, message } = mapTeamJoinError(err);
        flow.setError(code, message);
      } finally {
        setBusy(false);
      }
    },
    [flow, onRequestToJoin, state.emailOtpToken]
  );

  return (
    <div className={`${s.formContent} ${styles.content}`}>
      <header>
        <h2 id="self-service-smb-team-title" className={styles.title}>
          Pick your team
        </h2>
        <p className={styles.subtitle}>{STEP_COPY.team.joinSubtitle}</p>
      </header>
      {state.error && (
        <p role="alert" className={styles.errorText}>
          {state.error.message}
        </p>
      )}
      <ul className={styles.teamList}>
        {state.domainMatches.map((team) => (
          <li key={team.id} className={styles.teamItem}>
            <div>
              <p className={styles.teamName}>{team.name}</p>
              {team.memberCount !== undefined && team.memberCount !== null && (
                <p className={styles.teamMembers}>{team.memberCount} members</p>
              )}
            </div>
            <Button
              color="teal"
              size="normal"
              onClick={() => requestJoin(team)}
              disabled={busy}
              classes={{ root: s.continueButton }}
            >
              Request to join
            </Button>
          </li>
        ))}
      </ul>
      {/* Escape hatch — TLA+ contract (R-3): the team-join branch must
          always offer a path to team-create. requestToJoinGroup can reject
          for several reasons (inactive team, locked domain, already-pending
          membership) that leave the user stuck on this picker; the action
          flips the reducer back to team-create with cleared picker state. */}
      <div className={styles.escapeRow}>
        <Button
          variant="text"
          size="normal"
          onClick={() => flow.restartTeamFromJoin()}
          disabled={busy}
        >
          Create a new team instead
        </Button>
      </div>
    </div>
  );
}
